/*
 * Straight-to-printer labels need two Chrome machine policies on each packing PC (#737): SilentPrintingEnabled and a
 * DefaultPrinterSelection naming the label printer. A page cannot write or read machine policies, so the desk gets a
 * setup file instead: it sets both policies with Windows' administrator prompt, restarts Chrome and reopens the
 * admin with `?printSetup=done`. That mark is kept in the profile's storage, and the reminder reads it (one
 * synchronous localStorage read [10/14]) to stay away on a PC that was set up (SSD-7389). The reminder above every
 * Fulfillment screen and the Label printer part of View Orders' settings read and change the same state here, so a
 * change in one shows in the other at once.
 */

export const LABEL_PRINTER_NAME = 'Xprinter XP-DT108B LABEL';

/** The first Chrome that reads SilentPrintingEnabled. */
export const MIN_CHROME_MAJOR = 144;

export const SETUP_PARAM = 'printSetup';

export const SETUP_DONE_KEY = 'fulfillment.printSetup.doneOn';

export const PRINT_SETUP_HIDDEN_KEY = 'fulfillment.printSetupBanner.hiddenOn';

export const SETUP_FILE_NAME = 'SkinSeoul-Label-Printer-Setup.cmd';

const POLICY_KEY = 'HKLM\\SOFTWARE\\Policies\\Google\\Chrome';

const RETURN_PATH = '/fulfillment/viewOrders';

/** Today on the warehouse calendar (KST), as YYYY-MM-DD. */
export const kstToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

export const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const writeStored = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Storage blocked: the reminder simply shows again on the next load.
  }
};

let setUpThisLoad = false;

const CHANGE_EVENT = 'fulfillment-print-setup-change';

const changed = (): void => {
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/** Calls `listener` whenever the setup state changes here or in another tab. */
export const subscribePrintSetup = (listener: () => void): (() => void) => {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};

export interface PrintSetupState {
  /** The KST day the setup file finished on this Chrome, or null when it has not. */
  doneOn: string | null;
  /** The KST day the desk last chose "Don't show today", or null. */
  hiddenOn: string | null;
  /** This page load is the one the setup file opened. */
  justSetUp: boolean;
}

/** A string that changes whenever the state does, for useSyncExternalStore. */
export const printSetupSnapshot = (): string =>
  JSON.stringify([readStored(SETUP_DONE_KEY), readStored(PRINT_SETUP_HIDDEN_KEY), setUpThisLoad]);

export const parsePrintSetupSnapshot = (snapshot: string): PrintSetupState => {
  const [doneOn, hiddenOn, just] = JSON.parse(snapshot) as [string | null, string | null, boolean];
  return { doneOn, hiddenOn, justSetUp: just };
};

/** Whether this page load is the one the setup file opened, so the reminder can say the PC is ready. */
export const justSetUp = (): boolean => setUpThisLoad;

/** The desk has read the "set up" note; screens opened after it no longer show it. */
export const acknowledgeSetUp = (): void => {
  setUpThisLoad = false;
  changed();
};

/** "Don't show today": the reminder stays away on this Chrome until the next KST day. */
export const hidePrintSetupToday = (now: Date = new Date()): void => {
  writeStored(PRINT_SETUP_HIDDEN_KEY, kstToday(now));
  changed();
};

/** Brings the reminder back today after "Don't show today"; a set-up PC stays set up. */
export const showPrintSetupAgain = (): void => {
  writeStored(PRINT_SETUP_HIDDEN_KEY, null);
  changed();
};

/** Forgets that this Chrome was set up (a new PC, or a setting since removed): the reminder shows until the file runs again. */
export const resetPrintSetup = (): void => {
  writeStored(SETUP_DONE_KEY, null);
  writeStored(PRINT_SETUP_HIDDEN_KEY, null);
  setUpThisLoad = false;
  changed();
};

/**
 * Read at boot, before a login redirect can drop the query: `done` keeps the mark, `reset` clears it (to see the
 * reminder again on a PC, or to test it). Either way the parameter is taken off the address.
 */
export const recordPrintSetup = (now: Date = new Date()): void => {
  const url = new URL(window.location.href);
  const value = url.searchParams.get(SETUP_PARAM);
  if (value !== 'done' && value !== 'reset') {
    return;
  }
  if (value === 'done') {
    writeStored(SETUP_DONE_KEY, kstToday(now));
    setUpThisLoad = true;
    changed();
  } else {
    resetPrintSetup();
  }
  url.searchParams.delete(SETUP_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

export const printSetupDone = (): boolean => readStored(SETUP_DONE_KEY) !== null;

/** Chrome's major version from the user agent, or null outside Chrome. */
export const chromeMajor = (userAgent: string = window.navigator.userAgent): number | null => {
  if (/Edg\/|OPR\//.test(userAgent)) {
    return null;
  }
  const match = /Chrome\/(\d+)/.exec(userAgent);
  return match ? Number(match[1]) : null;
};

/** Run by hand in PowerShell opened as administrator, when the setup file cannot run on a PC. */
export const POLICY_COMMANDS = [
  "New-Item -Path 'HKLM:\\SOFTWARE\\Policies\\Google\\Chrome' -Force | Out-Null",
  "Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Policies\\Google\\Chrome' -Name SilentPrintingEnabled -Type DWord -Value 1",
  `Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Policies\\Google\\Chrome' -Name DefaultPrinterSelection -Type String -Value '{"kind":"local","namePattern":"${LABEL_PRINTER_NAME}"}'`,
].join('\n');

/**
 * The Windows setup file: a .cmd, ASCII and CRLF because cmd.exe reads it as such. It stops before touching anything on
 * a Chrome older than 144, asks Windows for administrator rights only for the two registry writes (the /policy pass),
 * then reads both values back exactly and checks the label printer exists. Only when all of that holds does it reopen
 * the admin with the done mark, so a PC whose setup fell short keeps seeing the reminder. Chrome is never killed: the
 * file waits until the desk has closed it, then starts it as the desk's own user (an elevated Chrome would open another
 * account's profile). Values reach PowerShell through environment variables so spaces and quotes survive.
 */
export const windowsSetupScript = (origin: string): string => {
  const policy = `{"kind":"local","namePattern":"${LABEL_PRINTER_NAME}"}`;
  const lines = [
    '@echo off',
    'setlocal',
    'rem SkinSeoul label printing: lets Chrome on this PC print labels straight to the label printer.',
    'rem Sets two Chrome machine policies, SilentPrintingEnabled and DefaultPrinterSelection, then reopens the admin.',
    `set "POLICY_KEY=${POLICY_KEY}"`,
    `set "LABEL_PRINTER=${LABEL_PRINTER_NAME}"`,
    `set "PRINTER_POLICY=${policy}"`,
    `set "RETURN_URL=${origin}${RETURN_PATH}?${SETUP_PARAM}=done"`,
    'set "SELF=%~f0"',
    'if /i "%~1"=="/policy" goto policy',
    '',
    'set "CHROME=%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe"',
    'if not exist "%CHROME%" set "CHROME=%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe"',
    'if not exist "%CHROME%" set "CHROME=%LocalAppData%\\Google\\Chrome\\Application\\chrome.exe"',
    'if not exist "%CHROME%" goto nochrome',
    `powershell -NoProfile -Command "if ([version](Get-Item $env:CHROME).VersionInfo.ProductVersion -lt [version]'${MIN_CHROME_MAJOR}.0') { exit 1 }"`,
    'if errorlevel 1 goto oldchrome',
    '',
    'echo SkinSeoul label printer setup',
    'echo.',
    'echo Windows will now ask for administrator permission. Choose Yes.',
    'powershell -NoProfile -Command "try { Start-Process -FilePath $env:SELF -ArgumentList \'/policy\' -Verb RunAs -Wait -ErrorAction Stop } catch { exit 1 }"',
    'powershell -NoProfile -Command "$p = Get-ItemProperty -Path \'HKLM:\\SOFTWARE\\Policies\\Google\\Chrome\' -ErrorAction SilentlyContinue; if (-not $p -or $p.SilentPrintingEnabled -ne 1 -or $p.DefaultPrinterSelection -ne $env:PRINTER_POLICY) { exit 1 }"',
    'if errorlevel 1 goto failed',
    'powershell -NoProfile -Command "if (-not (Get-Printer -Name $env:LABEL_PRINTER -ErrorAction SilentlyContinue)) { exit 1 }"',
    'if errorlevel 1 goto noprinter',
    'echo Chrome is set to print labels straight to "%LABEL_PRINTER%".',
    '',
    ':closechrome',
    'tasklist /FI "IMAGENAME eq chrome.exe" 2>nul | find /I "chrome.exe" >nul',
    'if errorlevel 1 goto openchrome',
    'echo.',
    'echo Chrome reads the new setting when it starts. Close every Chrome window,',
    'echo and quit Chrome from the taskbar tray if it keeps running, then press any key here.',
    'pause >nul',
    'goto closechrome',
    '',
    ':openchrome',
    'start "" "%CHROME%" "%RETURN_URL%"',
    'echo Done. Chrome is opening the admin; print one order to check the label comes out with no dialog.',
    'timeout /t 15',
    'exit /b 0',
    '',
    ':policy',
    'reg add "%POLICY_KEY%" /v SilentPrintingEnabled /t REG_DWORD /d 1 /f >nul',
    'reg add "%POLICY_KEY%" /v DefaultPrinterSelection /t REG_SZ /d "{\\"kind\\":\\"local\\",\\"namePattern\\":\\"%LABEL_PRINTER%\\"}" /f >nul',
    'exit /b',
    '',
    ':nochrome',
    'echo Google Chrome is not installed on this PC.',
    'pause',
    'exit /b 1',
    '',
    ':oldchrome',
    `echo This Chrome is older than ${MIN_CHROME_MAJOR}. Update it first: open chrome://settings/help, then run this file again.`,
    'pause',
    'exit /b 1',
    '',
    ':noprinter',
    'echo.',
    'echo Windows has no printer named "%LABEL_PRINTER%".',
    'echo Rename the label printer to exactly that in Windows Settings, Printers and scanners,',
    'echo then run this file again. Until then labels open the print dialog.',
    'pause',
    'exit /b 1',
    '',
    ':failed',
    'echo.',
    'echo The setting was not saved. Run this file again and choose Yes when Windows asks for permission.',
    'pause',
    'exit /b 1',
  ];
  return `${lines.join('\r\n')}\r\n`;
};

export const downloadSetupScript = (origin: string = window.location.origin): void => {
  const url = URL.createObjectURL(new Blob([windowsSetupScript(origin)], { type: 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = SETUP_FILE_NAME;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
